import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuthStore } from './store/authStore';
import { useOfflineSync } from './hooks/useOfflineSync';
import { Layout } from './components/Layout';
import { LoginPage } from './components/LoginPage';
import { ForgotPasswordPage } from './components/ForgotPasswordPage';
import { ResetPasswordPage } from './components/ResetPasswordPage';
import { Dashboard } from './components/Dashboard';
import { StudySession } from './components/StudySession';
import { QuizMode } from './components/QuizMode';
import { ProgressPage } from './components/ProgressPage';
import { WordBrowserPage } from './components/WordBrowserPage';
import { Leaderboard } from './components/Leaderboard';
import { AdminPage } from './components/AdminPage';
import { SettingsPage } from './components/SettingsPage';
import { fetchDailyStats } from './api/progress';
import { syncTimezone } from './api/gamification';
import { useState } from 'react';
import type { DailyStats } from './types';

const pageTransition = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] },
};

function AppContent() {
  const { isAuthenticated } = useAuthStore();
  const [stats, setStats] = useState<DailyStats | null>(null);
  const { pendingCount, isOnline } = useOfflineSync();

  useEffect(() => {
    if (isAuthenticated) {
      fetchDailyStats().then(setStats).catch(() => {});
      // Keep streak/goal day-rollover aligned to the user's local midnight
      syncTimezone().catch(() => {});
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Layout
      stats={stats ? {
        new_count: stats.new_count,
        learning_count: stats.learning_count,
        mastered_count: stats.mastered_count,
        xp: stats.xp,
        current_streak: stats.current_streak,
        words_studied_today: stats.words_studied_today ?? 0,
        daily_goal: stats.daily_goal ?? 20,
        streak_freezes: stats.streak_freezes ?? 0,
      } : undefined}
      pendingCount={pendingCount}
      isOnline={isOnline}
    >
      <AnimatePresence mode="wait">
        <Routes>
          <Route
            path="/dashboard"
            element={
              <motion.div key="dashboard" className="h-full" {...pageTransition}>
                <Dashboard />
              </motion.div>
            }
          />
          <Route
            path="/study"
            element={
              <motion.div key="study" className="h-full" {...pageTransition}>
                <StudySession />
              </motion.div>
            }
          />
          <Route
            path="/quiz"
            element={
              <motion.div key="quiz" className="h-full" {...pageTransition}>
                <QuizMode />
              </motion.div>
            }
          />
          <Route
            path="/progress"
            element={
              <motion.div key="progress" className="h-full" {...pageTransition}>
                <ProgressPage />
              </motion.div>
            }
          />
          <Route
            path="/words"
            element={
              <motion.div key="words" className="h-full" {...pageTransition}>
                <WordBrowserPage />
              </motion.div>
            }
          />
          <Route
            path="/leaderboard"
            element={
              <motion.div key="leaderboard" className="h-full" {...pageTransition}>
                <Leaderboard />
              </motion.div>
            }
          />
          <Route
            path="/settings"
            element={
              <motion.div key="settings" className="h-full" {...pageTransition}>
                <SettingsPage />
              </motion.div>
            }
          />
          <Route
            path="/admin"
            element={
              <motion.div key="admin" className="h-full" {...pageTransition}>
                <AdminPage />
              </motion.div>
            }
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AnimatePresence>
    </Layout>
  );
}

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;
